import React, { useMemo, useState, useCallback } from "react";
import { Button, Card, CardBody, CardHeader, Divider, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from "@nextui-org/react";
import { PlusIcon, PencilSquareIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import ControlledReactSelect from "../../../components/elements/ControlledReactSelect";
import ControlledInput from "../../../components/elements/ControlledInput";
import { getFetcher } from "../../../services/api";
import RootAdmin from "../../../components/layouts/RootAdmin";
import RoleAccess from "../../../enums/roles";
import useUsers from "../../../hooks/useUsers";
import useListCompany from "../../../hooks/useListCompany";
import ClientDataGrid from "../../../components/elements/ClientDataGrid";

export default function UserManagementPage() {
  const { dataCompany, isLoadingCompany } = useListCompany();
  const { data, isLoading, mutate } = useSWR(`/api/users/search-users?returnAll=true`, getFetcher);

  const {
    isEdit,
    loadingEdit,
    onClickEdit,
    onCloseForm,
    onSubmitForm,
    modalForm: { onOpenModalForm, isOpenModalForm, onOpenChangeModalForm },
    hookForm: {
      control,
      handleSubmit,
      watch,
      formState: { isSubmitting },
    },
  } = useUsers({ mutate });
  const rolesIds = watch("rolesIds");

  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Nama Lengkap" },
      { field: "email", headerName: "Email" },
      { field: "phoneNumber", headerName: "Nomor Telepon" },
      { field: "address", headerName: "Alamat" },
      { field: "roles", headerName: "Peran", valueGetter: (params, row) => row?.roles?.map((role) => role.role?.name).join(", ") },
      {
        field: "actions",
        headerName: "Aksi",
        renderCell: (params) => (
          <div className="flex gap-2">
            {params.row.fullName !== "Super Admin" && (
              <Button color="primary" isIconOnly size="sm" onPress={() => onClickEdit(params.row.id)}>
                <PencilSquareIcon className="size-4" />
              </Button>
            )}
          </div>
        ),
        sortable: false,
        filterable: false,
      },
    ],
    // onClickEdit dipakai di dalam renderCell, jadi wajib masuk dependensi.
    // Versi lama memakai [] sehingga memicu warning react-hooks/exhaustive-deps.
    [onClickEdit],
  );

  const filteredUsers = useMemo(() => {
    if (!data?.data) return [];

    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return data.data;

    // Pengaman ?? "" penting: satu pengguna dengan fullName bernilai null cukup
    // untuk membuat .toLowerCase() melempar TypeError dan memutihkan halaman.
    // Email ikut dicari karena sering lebih spesifik daripada nama.
    return data.data.filter((user) => {
      const nama = (user?.fullName ?? "").toLowerCase();
      const email = (user?.email ?? "").toLowerCase();
      return nama.includes(keyword) || email.includes(keyword);
    });
  }, [data?.data, searchQuery]);

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardHeader>Daftar Pengguna</CardHeader>
        <Divider />
        <CardBody>
          <div className="mb-5 flex flex-wrap items-center gap-4">
            <Button onPress={onOpenModalForm} size="md" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
              Tambah
            </Button>
            {/* Sebelumnya: style={{ alignItems: 'right' }} - "right" bukan nilai
                valid untuk align-items, jadi deklarasinya diabaikan browser. */}
            <div className="w-full sm:w-80">
              <Input
                size="md"
                variant="bordered"
                placeholder="Cari nama atau email"
                value={searchQuery}
                onChange={handleSearchChange}
                onClear={() => setSearchQuery("")}
                isClearable
                startContent={<MagnifyingGlassIcon className="size-4 text-default-400" />}
                aria-label="Cari pengguna"
              />
            </div>
          </div>
          <div className="h-[500px]">
            {/* rowCount mengikuti hasil filter. Versi lama memakai data?.total,
                yang tidak pernah cocok dengan jumlah baris yang tampil begitu
                pencarian diisi - penyaringannya dilakukan di client. */}
            <ClientDataGrid rows={filteredUsers} rowCount={filteredUsers.length} isLoading={isLoading} columns={columns} />
          </div>
        </CardBody>
      </Card>

      <Modal size="2xl" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{isEdit ? "Ubah Pengguna" : "Tambah Pengguna"}</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit(onSubmitForm)}>
                  {isEdit && loadingEdit ? (
                    <div className="w-full flex justify-center">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                        <ControlledInput label="Nama Lengkap" name="fullName" control={control} isRequired={true} />
                        <ControlledInput label="Email" name="email" control={control} type="email" isRequired={true} />
                        <ControlledInput label="Nomor Telepon" name="phoneNumber" control={control} isRequired={true} />
                        <ControlledInput label="Alamat" name="address" control={control} isRequired={true} />
                        {!isEdit && (
                          <>
                            <ControlledInput label="Password Baru" name="password" control={control} type="password" />
                            <ControlledInput label="Konfirmasi Password" name="confirmPassword" control={control} type="password" placeholder="Konfirmasi Password Baru" />
                          </>
                        )}
                        <ControlledReactSelect
                          label="Peran"
                          name="rolesIds"
                          control={control}
                          options={Object.entries(RoleAccess)
                            .filter(([key]) => key !== "SUPER_ADMIN")
                            .map(([, value]) => ({
                              value: value,
                              label: value,
                            }))}
                          isMulti
                        />
                        {rolesIds?.includes(RoleAccess.PENGELOLA) && (
                          <ControlledReactSelect label="Perusahaan" name="companyIds" control={control} options={dataCompany?.data?.map((company) => ({ value: company.id, label: company.name }))} isLoading={isLoadingCompany} isMulti />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* isDisabled, bukan disabled: NextUI Button tidak
                            mengenali prop `disabled`, jadi tombol tetap bisa
                            diklik dan submit bisa terkirim dua kali. */}
                        <Button type="submit" color="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
                          {isEdit ? "Simpan" : "Tambah"}
                        </Button>
                        <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                          Batal
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
